import { DeleteOutlined } from '@ant-design/icons';
import { Button, Checkbox, Flex, Input, InputNumber, Popconfirm } from 'antd';
import React, { useEffect } from 'react';

export type JsonDataTypeType = 'checkbox' | 'number' | 'string';

export type JsonParamDataRowType = {
    type: JsonDataTypeType;
    defaultValue?: any;
    widthPercent?: number;
};

export type JsonParamDataType = {
    [key: string]: JsonParamDataRowType;
};

export type EffectListType<T> = {
    getIsDelete?: (rowData: T) => boolean;
    getIsBlockEditField?: (rowData: T) => (keyof T)[];
};

interface IInputJsonDataRow {
    rowData: any;
    fieldJsonParam: JsonParamDataType;
    onChange: (rowData: any, deleteId?: string) => void;
    effectList?: EffectListType<any>;
    disabled?: boolean;
    isAccessUpdate?: boolean;
    isAccessCreate?: boolean;
    isAccessDelete?: boolean;
}

const InputJsonDataRow: React.FC<IInputJsonDataRow> = ({
    rowData,
    fieldJsonParam,
    onChange,
    effectList,
    disabled,
    isAccessUpdate,
    isAccessCreate,
    isAccessDelete,
}) => {
    const [internalRowData, setInternalRowData] = React.useState<any>();

    useEffect(() => {
        setInternalRowData(rowData);
    }, [rowData]);

    useEffect(() => {
        // need pause to avoid cycle loop
        setTimeout(() => {
            internalRowData && JSON.stringify(internalRowData) !== JSON.stringify(rowData) && onChange(internalRowData);
        });
    }, [internalRowData]);

    const changeValue = (keyField: string, value: any) => {
        const newInternalRowData = {
            ...internalRowData,
            [keyField]: value,
        };
        setInternalRowData(newInternalRowData);
    };

    const renderCell = (keyField: string, type: JsonDataTypeType, value: any) => {
        const disabledInput =
            effectList && effectList.getIsBlockEditField
                ? effectList.getIsBlockEditField(internalRowData).includes(keyField)
                : false;

        switch (type) {
            case 'checkbox':
                return (
                    <Checkbox
                        checked={value}
                        onChange={(value: any) => {
                            changeValue(keyField, value.target.checked);
                        }}
                        disabled={disabled || !(isAccessUpdate || isAccessCreate)}
                    />
                );
            case 'number':
                return (
                    <InputNumber
                        style={{ width: '100%' }}
                        value={value}
                        onChange={(value: any) => {
                            changeValue(keyField, value);
                        }}
                        disabled={disabledInput || disabled || !isAccessCreate}
                    />
                );
            case 'string':
                return (
                    <Input
                        value={value}
                        onChange={(value: any) => {
                            changeValue(keyField, value.target.value);
                        }}
                        disabled={disabledInput || disabled || !isAccessCreate}
                    />
                );
            default:
                return '';
        }
    };

    const renderActionCell = (disabledButton: boolean) => {
        return (
            <>
                {isAccessDelete && (
                    <Popconfirm
                        title="Delete the subtask"
                        description="Are you sure to delete this subtask?"
                        onConfirm={() => {
                            onChange(internalRowData, internalRowData.id);
                        }}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button shape="circle" disabled={disabledButton} icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                )}
            </>
        );
    };

    const sumWidthColumn = (listOfKeyField: string[]) => {
        let sumWidthColumn = 0;

        for (const keyField of listOfKeyField) {
            sumWidthColumn += fieldJsonParam[keyField].widthPercent || 0;
        }

        return sumWidthColumn;
    };

    const listOfKeyField = Object.keys(fieldJsonParam);

    const widthColumn = sumWidthColumn(listOfKeyField);

    return (
        <Flex justify={'space-between'} align={'center'} style={{ width: '100%' }}>
            {internalRowData &&
                listOfKeyField.map((keyField, index) => {
                    const width = fieldJsonParam[keyField].widthPercent || 100 - widthColumn - 10;

                    return (
                        <div key={index} style={{ width: `${width}%` }}>
                            {renderCell(keyField, fieldJsonParam[keyField].type, internalRowData[keyField])}
                        </div>
                    );
                })}
            {renderActionCell(
                effectList && effectList.getIsDelete && internalRowData
                    ? effectList.getIsDelete(internalRowData)
                    : false,
            )}
        </Flex>
    );
};

export default InputJsonDataRow;
